<template>
  <div :class="$style.container">
    <ModalHeader
      :handleClose="handleCancel"
      headerText="Enroll Your iOS Device"
    />
    <div :class="$style.modal">
      <div :class="$style.modalContent">
        <div :class="$style.modalBody">
          <div :class="$style.stepsInfoColumn">
            <ol :class="$style.list">
              <li>
                <CircularStepNumber
                  :class="$style.circle"
                  stepNumber="1"
                />
                <span :class="$style.textContent">
                  Using the camera app on your iOS device, aim the camera at the QR code.
                </span>
              </li>
              <li>
                <CircularStepNumber
                  :class="$style.circle"
                  stepNumber="2"
                />
                <span :class="$style.textContent">
                  When the QR code is in focus, the code is automatically scanned.
                </span>
              </li>
              <li>
                <CircularStepNumber
                  :class="$style.circle"
                  stepNumber="3"
                />
                <span :class="$style.textContent">
                  Tap the pop-up message that appears on your device to display this URL:
                  {{ formattedLink }}
                </span>
                <span :class="$style.textTip">
                  <div :class="$style.infoHelpContainer">
                    <IconTooltip
                      :iconClass="['jc-info-circle', $style.infoIcon]"
                    />
                  </div>
                  <span :class="$style.textTipTitle">Tip:</span>
                  If you are unable to scan the QR code or the URL did not pop up on your
                  device, tap the URL direct link under the code to download the profile.
                </span>
              </li>
              <li>
                <CircularStepNumber
                  :class="$style.circle"
                  stepNumber="4"
                />
                <span :class="$style.textContent">
                  On your device, tap "Allow" to download the enrollment profile and
                  follow the instructions on your device.
                </span>
              </li>
              <li>
                <CircularStepNumber
                  :class="$style.circle"
                  stepNumber="5"
                />
                <span :class="$style.textContent">
                  Open the Settings app on your device and Tap Profile Downloaded.
                </span>
              </li>
              <li>
                <CircularStepNumber
                  :class="$style.circle"
                  stepNumber="6"
                />
                <span :class="$style.textContent">
                  Tap Install in the upper right corner and follow the instructions to create
                  a partition on your device to store company data.
                  Your enrolled device now appears in JumpCloud’s Admin Portal.
                </span>
              </li>
            </ol>
          </div>
          <div>
            <div
              :class="$style.expirationTime"
            >
              <span
                data-test-id="QRCodeModal_expirationTime"
              >
                This enrollment URL expires at {{ expirationTime }}
              </span>
            </div>
            <div :class="$style.qrCodeColumn">
              <div :class="$style.qrCodeContainer">
                <p>Scan to enroll device</p>
                <img
                  v-if="!qrImageLoading"
                  key="qrImage"
                  :class="$style.qrCode"
                  :src="qrCodeSrc"
                >
              </div>
              <svg width="100" height="180" fill="none" xmlns="http://www.w3.org/2000/svg" :class="$style.phoneIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><rect width="100" height="180" rx="8" fill="#383838"/><rect x="5" y="8" width="90" height="143" rx="2" fill="#F1F3F4"/><path fill-rule="evenodd" clip-rule="evenodd" d="M30 63.75A3.75 3.75 0 0133.75 60h3.75a1.5 1.5 0 010 3h-3.75a.75.75 0 00-.75.75v3.75a1.5 1.5 0 01-3 0v-3.75zM66.25 60A3.75 3.75 0 0170 63.75v3.75a1.5 1.5 0 01-3 0v-3.75a.75.75 0 00-.75-.75H62.5a1.5 1.5 0 110-3h3.75zM30 96.25a3.75 3.75 0 003.75 3.75h3.75a1.5 1.5 0 000-3h-3.75a.75.75 0 01-.75-.75V92.5a1.5 1.5 0 10-3 0v3.75zM66.25 100A3.75 3.75 0 0070 96.25V92.5a1.5 1.5 0 10-3 0v3.75a.75.75 0 01-.75.75H62.5a1.5 1.5 0 100 3h3.75z" fill="#41C8C3"/><path fill="#fff" d="M40 70h10v10H40z"/><path fill="#fff" stroke="#000" stroke-width="2" d="M41 81h8v8h-8z"/><path fill="#000" d="M44 84h2v2h-2z"/><path fill="#fff" stroke="#000" stroke-width="2" d="M51 71h8v8h-8z"/><path fill="#fff" d="M50 80h10v10H50z"/><path fill="#000" d="M53 83h4v4h-4zM43 73h4v4h-4zM54 74h2v2h-2z"/><rect x="22" y="76" width="56" height="2" rx="1" fill="#E54852" fill-opacity=".75"/><circle cx="50" cy="165" r="8" fill="#F1F3F4"/></svg>
              <div
                :class="$style.directLink"
              >
                <span
                  :class="$style.linkSpan"
                >Direct Link:</span>
                <TextLink
                  :copyOnClick="false"
                  :href="qrLink"
                >
                  {{ formattedLink }}
                </TextLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CircularStepNumber from 'client/components/CircularStepNumber.vue';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ModalHeader from 'client/components/Modal/ModalComponents/ModalHeader.vue';
import TextLink from 'client/components/TextLink/TextLink.vue';
import moment from 'moment';
import qrcode from 'qrcode';

export default {
  name: 'QRCodeModal',
  components: {
    CircularStepNumber,
    IconTooltip,
    ModalHeader,
    TextLink,
  },
  props: {
    qrLink: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      qrCodeSrc: '',
      qrImageLoading: true,
      icon: 'userconsole/static/img/icon-phone-illustration.svg',
    };
  },

  computed: {
    expirationTime() {
      return moment().add(1, 'hours').format('hh:mm A');
    },

    formattedLink() {
      return `${this.qrLink.slice(0, 47)}...`;
    },
  },

  mounted() {
    this.initializeQrCodeSrc();
  },

  methods: {
    handleCancel() {
      ModalEventBus.close();
    },

    /**
     * QR code options see:
     * https://github.com/soldair/node-qrcode#qr-code-options
     */
    async initializeQrCodeSrc() {
      const qrCodeSrc = await qrcode.toDataURL(this.qrLink, {
        color: {
          light: '#0000', // Transparent background
        },
        errorCorrectionLevel: 'high',
        width: 160,
      }).catch(() => {
        // catch and do nothing
      });
      this.loadQrImage(qrCodeSrc);
    },

    loadQrImage(qrCodeSrc) {
      this.qrCodeSrc = qrCodeSrc;
      const image = new Image();
      image.onload = () => {
        this.qrImageLoading = false;
      };
      image.src = qrCodeSrc;
      return image;
    },
  },
};
</script>

<style module>
@value (jcPrimary50, jcInformationalBase) from '~css/brand-colors.css';
@value (max-width-medium, max-width-xx-large) from '~css/breakpoints.css';

.circle {
  text-indent: 0;
}

.directLink {
  grid-column-end: 5;
  grid-column-start: 1;
  grid-row-start: 7;
}

.infoHelpContainer {
  display: inline-block;
  margin-right: 0.375rem;
}

.infoIcon {
  border-bottom: 0;
  color: jcInformationalBase;
  font-size: 1.2rem;
}

.list {
  counter-reset: list-counter;
  list-style-type: none;
  margin-left: 0.5rem;
  text-indent: -3.5rem;
}

.list li {
  margin-bottom: 1.75rem;
}

.modalBody {
  column-gap: 1.25rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 2.3125rem;
}

.textContent {
  margin-left: 0.9375rem;
}

.textTip {
  display: block;
  font-size: 0.8rem;
  line-height: initial;
  margin-left: 1.875rem;
  margin-top: 0.9375rem;
  text-indent: initial;
}

.textTipTitle {
  font-weight: 700;
}

.qrCode {
  margin: 0 auto;
  width: 10rem;
}

.qrCodeContainer {
  align-items: center;
  background: jcPrimary50;
  border-radius: 0.625rem;
  display: grid;
  grid-column-end: 4;
  grid-column-start: 2;
  grid-row-end: 4;
  grid-row-start: 2;
  height: 15.6875rem;
  justify-content: center;
  width: 18.1875rem;
}

.expirationTime {
  text-align: center;
}

.qrCodeContainer p {
  font-weight: 700;
  margin-bottom: -2.1875rem;
  text-align: center;
}

.phoneIcon {
  grid-column-end: 4;
  grid-column-start: 1;
  grid-row-end: 4;
  grid-row-start: 3;
  max-width: 7.5rem;
  width: 27.8%;
}

@media (max-width: max-width-medium) {
  .modalBody .qrCodeColumn {
    display: block;
  }

  .modalBody {
    flex-direction: column;
  }

  .directLink {
    margin-top: 0.9375rem;
    text-align: center;
  }

  .list {
    line-height: inherit;
  }

  .phoneIcon {
    display: none;
  }

  .qrCodeContainer {
    background: none;
    display: block;
    height: initial;
    width: initial;
  }

  .qrCodeContainer p {
    margin: initial;
  }

  .qrCode {
    display: block;
    margin: 0 auto;
  }

  .textTip {
    margin-left: 0.625rem;
    margin-top: 0.625rem;
  }
}

@media (min-width: max-width-xx-large) {
  .modalContent {
    align-items: center;
    display: flex;
  }
}

.linkSpan {
  font-weight: 700;
}

.stepsInfoColumn {
  flex: 2;
}

.qrCodeColumn {
  display: grid;
  flex: 1;
  grid-template-columns: 18.6% 6.4% 64% 11%;
  grid-template-rows: 17% 21.5% 36% 33.5% 11% auto;
}
</style>
